import React, { useEffect, useMemo } from "react";
import {
  AdvancedTable,
  FILTER_TYPES,
  IFilterState,
  ITableState,
  Loader,
} from "taltech-styleguide";
import { map, partition, sortBy } from "ramda";
import { column_components } from "./column.components";
import { isFunction, isString } from "ramda-adjunct";
import { cols } from "./columns";
import { addOrIgnoreActionsColumn } from "./ActionColumn";
import { useWithDefaultTable } from "@components-system";
import { TableProps } from "@components-common/types";
import { getProps } from "@components-system";
import { TableProvider } from "./Provider";
import { TableState, useTableContext } from "./context";
import { useNotifications } from "@hooks";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../../_runtime/iam/session.context";
import { useLoading } from "../../components-common/_shared/loading/use-loading.hook";

/**
 * Function: isPaginationActive
 *
 * Determines if pagination is active based on the provided table properties.
 *
 * @param {object} params - The parameters for checking pagination.
 * @param {object} params.pagination - The pagination configuration.
 * @param {boolean} params.showPagination - Indicates if pagination should be shown.
 * @returns {boolean} - True if pagination is active, false otherwise.
 */
const isPaginationActive = ({
  pagination,
  showPagination,
}: Pick<TableProps, "pagination" | "showPagination">) =>
  pagination && showPagination;

/**
 * Function: getRemote
 *
 * Returns the remote configuration for the table based on the provided table properties.
 *
 * @param {TableProps} props - The table properties.
 * @returns {object} - The remote configuration.
 */
const getRemote = (props: TableProps) => ({
  pagination: isPaginationActive(props),
  sort: true,
  filter: true,
});

/**
 * Function: getPagination
 *
 * Returns the pagination configuration for the table based on the provided table properties and state.
 *
 * @param {TableProps} props - The table properties.
 * @param {TableState} state - The table state.
 * @returns {object} - The pagination configuration.
 */
const getPagination = ({ pagination }: TableProps, { query }: TableState) => {
  const result = {
    ...(pagination! as any),
    sizePerPage: query.sizePerPage,
    page: query.page,
    totalSize: query.total,
  };
  return result;
};

/**
 * Function: tablePropsToAdvancedTableProps
 *
 * Maps the table properties and state to the properties required by the AdvancedTable component.
 *
 * @param {TableProps} props - The table properties.
 * @param {TableState} state - The table state.
 * @returns {object} - The properties for the AdvancedTable component.
 */
export const tablePropsToAdvancedTableProps = (
  props: TableProps,
  state: TableState
) => {
  const { isLoading, handleTableChange, items } = state;
  const showPagination = isPaginationActive(props);
  if (props.source.type === "MEMORY") {
    return {
      keyField: "id",
      data: props.source.elements,
      columns: props.columns,
      loading: false,
      pagination: undefined,
    };
  }
  return {
    keyField: "id",
    data: items,
    remote: getRemote(props),
    callOnTableChangeOnMount: true,
    onTableChange: handleTableChange,
    columns: props.columns,
    loading: isLoading,
    pagination: showPagination ? getPagination(props, state) : undefined,
  };
};

/**
 * Component: TableTTInner
 *
 * This component renders the AdvancedTable component with the appropriate properties and context.
 *
 * @param {TableProps} props - The table properties.
 * @returns {JSX.Element} - The rendered AdvancedTable component.
 */
export const TableTTInner = (props: TableProps) => {
  const state = useTableContext();
  const userNotificationService = useNotifications();
  const nav = useNavigate();

  const memoizedColumns = useMemo(
    () =>
      addOrIgnoreActionsColumn(
        props.columns!,
        (props as any).actions,
        props.showRowActions,
        nav,
        state,
        userNotificationService,
        props.userRole
      ),
    [props.source.key]
  );
  const tableProps = {
    ...tablePropsToAdvancedTableProps(props, state),
    columns: memoizedColumns,
  };
  return <AdvancedTable {...tableProps} />;
};

/**
 * Component: RootieTable
 *
 * This component renders the TableTTInner component within a TableProvider context.
 * It handles the merging of default table properties and user context.
 *
 * @param {TableProps} propsParam - The initial table properties.
 * @returns {JSX.Element} - The rendered TableTTInner component.
 */
export const RootieTable = (propsParam: TableProps) => {
  const { role } = useUserContext();
  const props: any = getProps(
    propsParam as TableProps & {
      showRowActions: boolean;
      showPagination: boolean;
    }
  );

  if (typeof props.source.url !== "string") {
    if (typeof props.source.elements[0] === "function") {
      props.source.url = props.source.elements[0](role);
      props.source.type = "REMOTE";
      if (props.source.url === "/coursesms/assignments/self") {
        props.source.urlParams = { courseId: "{id}" };
      }
    }
  }

  const [loading, setLoading] = useLoading(true);

  useEffect(() => {
    setLoading(true);
  }, [props.source.key]);

  useEffect(() => {
    if (loading) setLoading(false);
  }, [loading]);

  const type = ((propsParam as any)?.type ||
    (props as any)?.type ||
    "viewer") as string;
  const mergedProps = useWithDefaultTable(type, props) as any;
  const { columns, ...rest } = mergedProps;

  const columnMergedProps = useMemo(() => {
    const parts = partition(isString, columns || []);
    const resultString = map(
      (x: string) => (column_components as any)[x],
      parts[0] as any
    );
    const resultObject = map(
      (x: any) =>
        isFunction((cols as any)[x.id])
          ? (cols as any)[x.id](x)
          : (cols as any)[x.id],
      parts[1] as any
    ).filter((x: any) => x !== undefined);
    return sortBy(
      (x: any) => x.order,
      [...resultString, ...(resultObject as any)]
    );
  }, [columns]);

  return loading ? (
    <Loader overlay />
  ) : (
    <TableProvider
      key={props.source.key}
      id={props.source.key}
      props={{ ...rest }}
    >
      <TableTTInner
        key={props.source.key}
        id={props.source.key}
        {...rest}
        userRole={role}
        columns={columnMergedProps}
      />
    </TableProvider>
  );
};
